import { Card, Checkbox, Descriptions, Space, Upload, message, Alert, Typography, Button } from 'antd';
import { RcFile } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { PrimaryButton, SecondaryButton } from 'components/button';
import { CardTitle } from 'components/claimCard/CardTitle';
import { StyledTitle } from 'components/StyledTitle';
import moment from 'moment';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDatePicker, UploadInput, DEFAULT_MAXIMUM_FILES, FormWrapper } from 'components/form';
import styled from 'styled-components';
import { DEFAULT_DATE_FORMAT } from 'utils';
import Base64Utils from 'utils/Base64';
import { formatCurrency } from 'utils/currency';
import { isLocaleEnglish } from 'utils/languageUtils';
import { ClaimFormState, excludeRemovedDocuments } from '../schema';
import { Markdown } from 'components';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ClaimInfoCardUtils } from './ClaimInfoCard/utils';
import { SelectedClaimTypeObjectType } from '../hooks/useSelectedClaimTypeMapping';
import { ClaimTypesEnum } from '../../claims/components/FilterSection/utils';
import type { RangePickerProps } from 'antd/es/date-picker';
import { DoctorSelect } from './ClaimInfoCard/DoctorSelect';
import { DiagnosisSelect } from './ClaimInfoCard/DiagnosisSelect';
import ReceiptItemModal from './ReceiptItemModal';
import { CurrencySelect } from './CurrencySelect';
import InfoIcon from 'assets/images/icon-info.svg';
import { OCRResult, ReceiptItem } from 'all-in-one-shared-api/dist/apis/claims/interfaces';
import './claim.less';
import { isEmpty } from 'lodash';
import { MessageType } from '../../../models/MessageType';

const { Text } = Typography;
const BravoEditIcon = styled(EditOutlined)`
  color: var(--primary-color);
  font-size: 16px;
`;

const BravoRemoveIcon = styled(DeleteOutlined)`
  color: var(--primary-color);
  font-size: 16px;
`;

const { Item: DescriptionsItem } = Descriptions;

const ShowUploadList = styled(Upload)`
  .ant-upload-list-item {
    margin-top: 0;
  }
`;

const Wrapper = styled.div`
  display: grid;
  gap: 24px;
`;
const SecondaryDiv = styled.div`
  display: flex;
  align-items: start;
  gap: 6px;
`;
const BenefitTypeWrapper = styled(Descriptions)`
  .ant-descriptions-item-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .ant-descriptions-item-label {
      display: inline-block;
    }
    .ant-descriptions-item-content {
      display: inline-block;
    }
  }
`;

interface DescriptionsItemProps {
  label: string;
  value?: string;
  children?: React.ReactNode;
  span?: number;
}

interface ReviewPageProps {
  handleOnBack: () => void;
  isLoading?: boolean;
  selectedClaimTypeObject: SelectedClaimTypeObjectType;
  ocrData?: OCRResult;
  ocrErrorTips?: string | null;
}

export const ReviewPage = ({
  handleOnBack,
  isLoading,
  selectedClaimTypeObject,
  ocrData,
  ocrErrorTips,
}: ReviewPageProps) => {
  const { t, i18n } = useTranslation();
  const {
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ClaimFormState>();
  // const claimTypeError = errors.claimTypeObject as unknown;
  // if (claimTypeError && typeof claimTypeError === 'object' && claimTypeError !== null && 'message' in claimTypeError) {
  //   const errorObj = claimTypeError as { message?: string };
  //   console.log(errorObj.message);
  // }

  const [isAgreed, setIsAgreed] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [receiptItems, setReceiptItems] = useState<ReceiptItem[]>(
    ocrData?.claimType.claimTypeCode === 'OP'
      ? ocrData.receiptItems
          .filter((item) => item.benefitCode)
          .map((benefit) => ({ ...benefit, benefitCode: `__${benefit.benefitCode}` }))
      : [],
  );
  const [receiptAmounts, setReceiptAmounts] = useState<string[]>(
    ocrData?.receiptItems.map((item) => item.benefitAmount) ?? [],
  );

  const values = getValues();
  const [disableUpload, setDisableUpload] = useState(false);

  useEffect(() => {
    if (ocrData && !isEmpty(ocrData.claimType) && ocrData.claimType.claimTypeCode) {
      if (ocrData.receiptItems.length == 0) {
        if (ocrData.claimType.claimTypeCode === 'OP') {
          setReceiptItems([]);
          setReceiptAmounts([]);
        } else {
          setReceiptItems([
            {
              benefitCode: ocrData.claimType.claimTypeCode,
              benefitDescription: ocrData.claimType.claimTypeDescription,
              benefitChineseDescription: ocrData.claimType.claimTypeChineseDescription,
              benefitAmount: ocrData.totalAmount,
              benefitSpecialty: ocrData.claimType.claimTypeSpecialty,
            },
          ]);
          setReceiptAmounts([ocrData.totalAmount]);
        }
      }
    }
  }, [ocrData]);

  const handleOnPreview = async (file: UploadFile) => {
    try {
      // 检查文件是否有 originFileObj（实际文件对象）
      const fileToPreview = file.originFileObj || file;

      if (fileToPreview) {
        // 获取文件的 Base64 数据
        const base64Data = await Base64Utils.getBase64FromRcFile(fileToPreview as RcFile);

        // 创建新窗口并显示文件内容
        const win = window.open('', '_blank');
        if (win) {
          // 根据文件类型决定显示方式
          if (file.type?.includes('image/')) {
            // 图片文件：直接显示图片
            win.document.write(`
              <html>
                <head><title>${file.name || 'Preview'}</title></head>
                <body style="margin:0; padding:20px; display:flex; justify-content:center; align-items:center; min-height:100vh; background:#f5f5f5;">
                  <img src="${base64Data}" style="max-width:100%; max-height:90vh; object-fit:contain;" alt="${
              file.name
            }" />
                </body>
              </html>
            `);
          } else if (file.type === 'application/pdf') {
            // PDF 文件：使用 iframe 显示
            win.document.write(`
              <html>
                <head><title>${file.name || 'PDF Preview'}</title></head>
                <body style="margin:0; padding:0;">
                  <iframe src="${base64Data}" style="width:100%; height:100vh; border:none;" />
                </body>
              </html>
            `);
          }
          win.document.close();
        }
      }
    } catch (error) {
      console.error('文件预览失败:', error);
    }
  };
  const handleEdit = (i: number) => {
    setEditingIndex(i);
    setIsModalVisible(true);
  };
  const handleRemove = (i: number) => {
    const newReceiptItems = [...receiptItems];
    newReceiptItems.splice(i, 1);
    const newReceiptAmounts = [...receiptAmounts];
    newReceiptAmounts.splice(i, 1);

    // 先更新状态
    setReceiptItems(newReceiptItems);
    setReceiptAmounts(newReceiptAmounts);

    // 然后立即更新 form 值，确保使用最新的 receiptItems
    setValue(
      'claimTypeObject',
      newReceiptItems.map((item) => item.benefitCode),
    );
    setValue('receiptAmounts', newReceiptAmounts);
  };
  const addRow = () => {
    setEditingIndex(null);
    setIsModalVisible(true);
  };
  const handleModalSubmit = (item: ReceiptItem) => {
    const newItem = {
      benefitCode: item.benefitCode,
      benefitDescription: item?.benefitDescription ?? '',
      benefitChineseDescription: item?.benefitChineseDescription ?? '',
      benefitAmount: item.benefitAmount,
      benefitSpecialty: item?.benefitSpecialty ?? '',
    };
    // 检查是否需要清空已有数据
    const specialBenefitCodes = ['DEN', 'HOSP', 'MAT'];
    let updatedReceiptItems: ReceiptItem[] = [];
    let updatedReceiptAmounts: string[] = [];

    if (specialBenefitCodes.includes(newItem.benefitCode)) {
      // 如果是特殊类型，删除所有已有数据，只保留最新的 newItem
      updatedReceiptItems = [newItem];
      updatedReceiptAmounts = [newItem.benefitAmount];
    } else {
      if (editingIndex !== null) {
        // 编辑现有项（普通类型）
        const newReceiptItems = [...receiptItems];
        newReceiptItems[editingIndex] = newItem;
        const newReceiptAmounts = [...receiptAmounts];
        newReceiptAmounts[editingIndex] = newItem.benefitAmount;
        updatedReceiptItems = newReceiptItems;
        updatedReceiptAmounts = newReceiptAmounts;
      } else {
        // 添加新项（普通类型）
        // 检查是否有重复的 benefitCode
        const existingIndex = receiptItems.findIndex((item) => item.benefitCode === newItem.benefitCode);
        if (existingIndex !== -1) {
          // 如果有重复，替换对应位置的项
          const newReceiptItems = [...receiptItems];
          newReceiptItems[existingIndex] = newItem;
          const newReceiptAmounts = [...receiptAmounts];
          newReceiptAmounts[existingIndex] = newItem.benefitAmount;
          updatedReceiptItems = newReceiptItems;
          updatedReceiptAmounts = newReceiptAmounts;
        } else {
          // 如果没有重复，新增项
          updatedReceiptItems = [...receiptItems, newItem];
          updatedReceiptAmounts = [...receiptAmounts, newItem.benefitAmount];
        }
      }
    }
    // 先更新状态
    setReceiptItems(updatedReceiptItems);
    setReceiptAmounts(updatedReceiptAmounts);

    // 然后立即更新 form 值，确保使用最新的 receiptItems
    setValue(
      'claimTypeObject',
      updatedReceiptItems.map((item) => item.benefitCode),
    );
    setValue('receiptAmounts', updatedReceiptAmounts);

    setIsModalVisible(false);
    setEditingIndex(null);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingIndex(null);
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    if (!current) return false;

    const oneYearAgo = moment().subtract(1, 'years').subtract(1, 'days');
    return current.isAfter(moment().endOf('day')) || current.isBefore(oneYearAgo);
  };
  const benefitType: DescriptionsItemProps[] = [
    {
      label: t('newClaim.reviewInfo.benefitType'),
      children: (
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Text
            style={{ width: '40%', color: 'var(--text-color-secondary)', textAlign: 'right', paddingRight: '20px' }}
          >
            {t('newClaim.reviewInfo.receiptAmount')}
          </Text>
          <CurrencySelect name="currency" label={''} wrapStyles={{ width: '20%', gap: 0 }} />
        </div>
      ),
    },
    ...(receiptItems?.length
      ? receiptItems.map((item, i) => ({
          label: isLocaleEnglish(i18n.language) ? item.benefitDescription : item.benefitChineseDescription,
          span: 2,
          children: (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              <div style={{ width: '40%', textAlign: 'right', paddingRight: '20px' }}>
                <span style={{ marginLeft: '12px' }}>
                  {item.benefitAmount ? formatCurrency(item.benefitAmount, 2) : ''}
                </span>
              </div>
              <div style={{ width: '20%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <BravoEditIcon onClick={() => handleEdit(i)}></BravoEditIcon>
                {receiptItems.length != 1 && (
                  <BravoRemoveIcon onClick={() => handleRemove(i)} style={{ marginLeft: '25px' }}></BravoRemoveIcon>
                )}
              </div>
            </div>
          ),
        }))
      : []),
  ];

  const receipts = useMemo(() => excludeRemovedDocuments(values.receipts) as RcFile[], [values.receipts]);
  const otherDocuments = useMemo(
    () => excludeRemovedDocuments(values.otherDocuments) as RcFile[],
    [values.otherDocuments],
  );
  const referralLetters = useMemo(
    () => excludeRemovedDocuments(values.referralLetters) as RcFile[],
    [values.referralLetters],
  );
  const handleDisableUploadDocuments = useCallback(() => {
    const receiptsAmount = receipts ? excludeRemovedDocuments(receipts).length : 0;
    const referralLettersAmount = referralLetters ? excludeRemovedDocuments(referralLetters).length : 0;
    const otherDocumentsAmount = otherDocuments ? excludeRemovedDocuments(otherDocuments).length : 0;
    const totalDocuments = receiptsAmount + otherDocumentsAmount + referralLettersAmount;
    let disabled = false;
    if (totalDocuments >= DEFAULT_MAXIMUM_FILES) {
      disabled = true;
    }
    setDisableUpload(disabled);
    if (referralLettersAmount !== DEFAULT_MAXIMUM_FILES && otherDocumentsAmount !== DEFAULT_MAXIMUM_FILES && disabled) {
      message[MessageType.WARNING](t('common.error.noOfFilesExceeds', { amount: DEFAULT_MAXIMUM_FILES }));
    }
  }, [receipts, referralLetters, otherDocuments, t]);

  useEffect(() => {
    handleDisableUploadDocuments();
  }, [handleDisableUploadDocuments]);

  useEffect(() => {
    const hasReferralLetters = (selectedClaimTypeObject?.requiredDocs || []).length > 1;
    if (!hasReferralLetters && referralLetters?.length) {
      setValue('referralLetters', []);
    }
  }, [referralLetters?.length, selectedClaimTypeObject?.requiredDocs, setValue]);
  const requiredDocs: DescriptionsItemProps[] = [
    {
      label: (values?.requiredDocs && values?.requiredDocs[0]) || t('newClaim.supplementaryDocuments.receipt'),
      children: (
        <ShowUploadList
          defaultFileList={receipts}
          showUploadList={{ showRemoveIcon: false }}
          onPreview={handleOnPreview}
        />
      ),
    },
    ...((selectedClaimTypeObject?.requiredDocs || []).length > 1
      ? [
          {
            label: '',
            children: (
              <FormWrapper
                name="referralLetters"
                label={(values?.requiredDocs && values?.requiredDocs.slice(1).join(',')) || ''}
                wrapStyles={{ width: '100%' }}
              >
                <UploadInput
                  accept="image/*,application/pdf"
                  name="referralLetters"
                  allowRemoveDocuments={true}
                  disabled={disableUpload}
                  onPreview={handleOnPreview}
                />
              </FormWrapper>
            ),
          },
        ]
      : []),
    ...[
      {
        label: '',
        children: (
          <FormWrapper
            label={t('newClaim.supplementaryDocuments.otherDocument') + t('common.optional')}
            name="otherDocuments"
            secondaryText={t('newClaim.supplementaryDocuments.uploadingOtherDocumentInfo.description')}
            secondaryTextIcon={<img src={InfoIcon} alt="receiptsIcon" style={{ marginTop: '4px' }} />}
            wrapStyles={{ width: '100%' }}
          >
            <UploadInput
              accept="image/*,application/pdf"
              name="otherDocuments"
              allowRemoveDocuments={true}
              disabled={disableUpload}
              onPreview={handleOnPreview}
            />
          </FormWrapper>
        ),
      },
    ],
  ];
  const prevClaimSettlements = useMemo(
    () => excludeRemovedDocuments(values.prevClaimSettlements) as RcFile[],
    [values.prevClaimSettlements],
  );

  const partialClaims: DescriptionsItemProps[] = [
    ...(values.isFirstClaim || values.firstClaimWithGenerali
      ? [
          {
            label: t('newClaim.partialClaim.firstClaim.anotherGeneraliPolicyNoTitle'),
            value: values.anotherPolicyNumber,
          },
          {
            label: t('newClaim.partialClaim.firstClaim.anotherGeneraliMemberIdTitle'),
            value: values.anotherMemberId,
          },
        ]
      : []),
    ...(values.isFirstClaim === false && values.firstClaimWithGenerali === false
      ? [
          { label: t('newClaim.partialClaim.secondClaim.insurerNameTitle'), value: values.otherInsurerName },
          {
            label: t('newClaim.partialClaim.secondClaim.claimAmountTitle'),
            value: `${values.firstClaimCurrency || ''} ${
              values.firstClaimAmount ? formatCurrency(values.firstClaimAmount) : 0
            }`.trim(),
          },
          ...(prevClaimSettlements.length
            ? [
                {
                  label: t('newClaim.partialClaim.secondClaim.settlementAdviceTitle'),
                  children: (
                    <ShowUploadList
                      defaultFileList={prevClaimSettlements}
                      showUploadList={{ showRemoveIcon: false }}
                      onPreview={handleOnPreview}
                    />
                  ),
                },
              ]
            : []),
        ]
      : []),
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Wrapper className="review-page">
      {ocrErrorTips && <Alert message={t(`${ocrErrorTips}`)} type="error" showIcon closable />}
      <Card title={<CardTitle title={t('newClaim.reviewInfo.title')} />}>
        <Space direction="vertical">
          <BenefitTypeWrapper
            column={1}
            title={
              <>
                <StyledTitle level={4} margin="0 0 20px ">
                  {t('newClaim.reviewInfo.claimsDetails')}
                </StyledTitle>
                <StyledTitle level={5} style={{ margin: '0 0 20px !important' }}>
                  {t('newClaim.reviewInfo.claimsFor', { patientName: values.patientName })}
                </StyledTitle>
              </>
            }
            colon={false}
            labelStyle={{ color: 'var(--text-color-secondary)', width: '50%' }}
            contentStyle={{ width: '50%', alignItems: 'center' }}
          >
            {benefitType.map(
              ({ label, value, children, span }) =>
                (value || children) && (
                  <DescriptionsItem
                    span={span ?? 1}
                    key={label}
                    label={label}
                    contentStyle={{
                      width: '65%',
                    }}
                    labelStyle={{ width: '35%' }}
                  >
                    {value}
                    {children}
                  </DescriptionsItem>
                ),
            )}
          </BenefitTypeWrapper>
          {(watch('claimType') == 'OP' || receiptItems.length == 0) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '26%' }}>
              {/* <PlusCircleOutlined style={{ fontSize: '20px' }} onClick={() => addRow()} /> */}
              <Button type="primary" onClick={() => addRow()}>
                {t('newClaim.reviewInfo.addTypes')}
              </Button>
            </div>
          )}

          {errors.claimTypeObject ? (
            <div style={{ color: '#ff4d4f', fontSize: '14px' }}>
              {String((errors.claimTypeObject as { message?: string })?.message)}
            </div>
          ) : errors.receiptAmounts ? (
            <div style={{ color: '#ff4d4f', fontSize: '14px' }}>{t('newClaim.start.amountError')}</div>
          ) : null}
          <div style={{ margin: '24px 0' }}>
            <ReceiptItemModal
              isModalVisible={isModalVisible}
              handleModalClose={handleModalClose}
              handleModalSubmit={handleModalSubmit}
              editingItem={editingIndex !== null ? receiptItems[editingIndex] : null}
              isEditing={editingIndex !== null}
            />
          </div>
          {(!ocrErrorTips || receiptItems.length > 0) && (
            <FormWrapper
              name="treatmentDate"
              label={t(
                ClaimInfoCardUtils.getTitleTreatmentBasedOnClaimTypeForOcr(
                  (selectedClaimTypeObject.type == 'OP'
                    ? selectedClaimTypeObject.type
                    : selectedClaimTypeObject.data[0]?.code) as ClaimTypesEnum,
                ),
              )}
              secondaryTextIcon={<img src={InfoIcon} alt="infoIcon" style={{ marginTop: '4px' }} />}
              secondaryText={`${t('newClaim.start.partialClaim.infoTitle')} ${t(
                'newClaim.start.partialClaim.infoDescription',
              )}`}
            >
              <FormDatePicker
                name="treatmentDate"
                placeholder={t('newClaim.claimInfo.treatmentDatePlaceholder')}
                format={DEFAULT_DATE_FORMAT}
                disabledDate={disabledDate}
              />
            </FormWrapper>
          )}

          {(!ocrErrorTips || watch('treatmentDate') || getValues('doctorId') || getValues('doctorName')) && (
            <DoctorSelect selectedClaimTypeObject={selectedClaimTypeObject.data} />
          )}
          {(!ocrErrorTips || getValues('doctorId') || getValues('doctorName') || getValues('diagnosisCode')) && (
            <DiagnosisSelect selectedClaimTypeObject={selectedClaimTypeObject.data} />
          )}
          <Descriptions
            column={1}
            title={<StyledTitle level={5}>{t('newClaim.supplementaryDocuments.title')}</StyledTitle>}
            labelStyle={{ color: 'var(--text-color-secondary)' }}
          >
            <Text>{t('newClaim.supplementaryDocuments.description')}</Text>
            {requiredDocs.map(
              ({ label, value, children }) =>
                (value || children) && (
                  <DescriptionsItem key={label} label={label}>
                    {value}
                    {children}
                  </DescriptionsItem>
                ),
            )}
          </Descriptions>
          {!!partialClaims.length && (
            <Descriptions
              column={1}
              title={<StyledTitle level={5}>{t('newClaim.partialClaim.title')}</StyledTitle>}
              labelStyle={{ color: 'var(--text-color-secondary)' }}
            >
              {partialClaims.map(
                ({ label, value, children }) =>
                  (value || children) && (
                    <DescriptionsItem key={label} label={label}>
                      {value}
                      {children}
                    </DescriptionsItem>
                  ),
              )}
            </Descriptions>
          )}
          <Checkbox checked={isAgreed} onChange={(e: CheckboxChangeEvent) => setIsAgreed(e.target.checked)}>
            <Markdown linkTarget={'_blank'}>{t('newClaim.reviewInfo.agreementTo', { lan: i18n.language })}</Markdown>
          </Checkbox>
        </Space>
        {(() => {
          const treatmentDate = watch('treatmentDate');
          if (!treatmentDate || !ocrData?.glmedclmDays) return false;
          const treatmentMoment = moment(treatmentDate, 'DD/MM/YYYY');
          const currentMoment = moment();
          const daysDiff = currentMoment.diff(treatmentMoment, 'days');
          return daysDiff > Number(ocrData.glmedclmDays);
        })() && (
          <SecondaryDiv>
            <img src={InfoIcon} alt="receiptsIcon" style={{ marginTop: '4px' }} />
            <Text>{t('newClaim.claimSuccess.exceed90', { days: ocrData?.glmedclmDays })}</Text>
          </SecondaryDiv>
        )}
        {watch('receiptAmounts')
          .map((amount) => parseFloat(amount) || 0)
          .reduce((sum, amount) => sum + amount, 0) > (Number(ocrData?.receiptWaiverAmount?.amount) || 8000) && (
          <SecondaryDiv>
            <img src={InfoIcon} alt="receiptsIcon" style={{ marginTop: '4px' }} />
            <Text>
              {t('newClaim.claimSuccess.overAmount', {
                amount: ocrData?.receiptWaiverAmount?.amount || 8000,
              })}
            </Text>
          </SecondaryDiv>
        )}
      </Card>
      <Space>
        <SecondaryButton onClick={handleOnBack}>{t('common.back')}</SecondaryButton>
        <PrimaryButton disabled={!isAgreed || isLoading} htmlType="submit" loading={isLoading}>
          {t('common.submit')}
        </PrimaryButton>
      </Space>
    </Wrapper>
  );
};
